import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import { createPortal } from 'react-dom';
import { canUseDOM } from '@qover/utils/src/dom';

const modalRoot =
  typeof document !== 'undefined' && document.getElementById('modal-root');

const ModalOverlay = styled.div`
  align-items: center;
  animation-name: fadeIn;
  background-color: ${({ theme }) => theme.colors.textQuote(0.9)};
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background-color: #fff;
  border-radius: 3px;
  height: auto;
  max-height: 100vh;
  max-width: 600px;
  overflow: hidden;
  overflow-y: auto;
  padding: 0 10px 10px 10px;
  position: relative;
  width: 100%;
  z-index: 10001;

  @media (min-width: ${({ theme }) => theme.breakpoints.minWidth.S}) {
    padding: 0 54px 54px 54px;
  }
`;

type ModalContainerProps = {
  hideOverflow: boolean;
  isHidden: boolean;
};

function ModalContainer(props: React.PropsWithChildren<ModalContainerProps>) {
  const el = useRef<HTMLDivElement | undefined>(
    typeof document !== 'undefined' ? document.createElement('div') : undefined,
  );
  const { hideOverflow = false } = props;

  useEffect(() => {
    if (el.current && modalRoot) {
      modalRoot.appendChild(el.current);
    }
    if (hideOverflow && document?.body?.classList?.add) {
      document.body.classList.add('modal-open');
    }

    return () => {
      if (el.current && modalRoot) {
        modalRoot.removeChild(el.current);
      }
      if (hideOverflow && document?.body?.classList?.remove) {
        document.body.classList.remove('modal-open');
      }
    };
  }, []);

  if (props.isHidden || !canUseDOM || !el.current) {
    return null;
  }
  // Use a portal to render the children into the element
  return createPortal(
    // Any valid React child: JSX, strings, arrays, etc.
    props.children,
    // A DOM element
    el.current,
  );
}

type ModalProps = {
  className?: string;
  hideOverflow?: boolean;
  onClickOutside?: () => void;
  style?: React.CSSProperties;
  overlayStyle?: React.CSSProperties;
  isHidden?: boolean;
};

export function Modal(props: React.PropsWithChildren<ModalProps>) {
  const {
    children,
    className,
    hideOverflow,
    onClickOutside,
    style = {},
    overlayStyle = {},
    isHidden = false,
  } = props;
  return (
    <ModalContainer hideOverflow={!!hideOverflow} isHidden={isHidden}>
      <ModalOverlay
        className="animated fadeIn"
        style={overlayStyle}
        onClick={onClickOutside}
      >
        <ModalContent
          className={cx('animated fadeInUp', className)}
          onClick={e => e.stopPropagation()}
          style={style}
        >
          {children}
        </ModalContent>
      </ModalOverlay>
    </ModalContainer>
  );
}
